<!--
  @component Navbar.Drawer

  A component that renders a generic sidebar drawer for the navbar.
  It appears when the drawer is opened and animates in from the specified direction.

  @prop colorVariant - The color variant for the drawer background and text
  @prop enterFrom - The direction from which the drawer enters ('left' or 'right', default is 'right')
  @prop css - Custom CSS styles to apply to the drawer

  Example:
  <Navbar.Drawer colorVariant="primary" enterFrom="right">
    <h2>Sidebar Content</h2>
    <p>This is a generic sidebar that can contain any content.</p>
  </Navbar.Drawer>
-->

<script lang="ts">
  import { getContext } from 'svelte';
  import { css } from '@agapi/ui/panda/css';
  import { drawerStyles } from '../navbar.styles';
  import { dynamicColorStyles } from '@agapi/ui/panda/utils';
  import { fly } from 'svelte/transition';
  import { cubicOut } from 'svelte/easing';
  import type { NavbarStore } from '../navbar-store.svelte';
  import type { NavbarDrawerProps } from '../navbar.types';

  /** Reference to the drawer element */
  let drawerRef: HTMLElement | null = $state(null);

  /** Props for the Navbar.Drawer component */
  const { children, colorVariant = 'default', enterFrom = 'right', fullHeight = false, css: cssProp }: NavbarDrawerProps = $props();

  /** Styles for the drawer */
  const styles = drawerStyles.raw();
  /** Navbar store from context */
  const navbarStore = getContext<NavbarStore>('navbar');
  navbarStore.setDrawerFullHeight(fullHeight);
</script>

{#if navbarStore.isDrawerOpen}
  <aside
    bind:this={drawerRef}
    class={css(styles, cssProp)}
    style="
      {colorVariant ? 'background-color: ' + dynamicColorStyles(colorVariant).backgroundColor + ';' : ''}
      {colorVariant ? 'color: ' + dynamicColorStyles(colorVariant).color + ';' : ''}
      {navbarStore.isDrawerFullHeight ? 'height: 100dvh; min-height: 100dvh; z-index: 12;' : ''}
    "
    transition:fly={{
      duration: 430,
      x: enterFrom === 'right' ? drawerRef?.clientWidth : -drawerRef?.clientWidth,
      opacity: 1,
      easing: cubicOut
    }}
  >
    {#if children}
      {@render children()}
    {/if}
  </aside>
{/if}
